import React, {FC} from 'react';
import styled from 'styled-components';

import {
  buildArticlePath,
  buildKnowledgeBaseArticlePreviewPath,
  buildPublicArticleUrl,
  buildPublishedInternalArticlePreviewLink,
} from '../../../../../libs/knowledge-base/shared/helpers/urlHelpers';
import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  KnowledgeBaseAccessTypesEnum,
  KnowledgeBaseSupportedLanguagesTypesEnum,
} from '../../../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {getKnowledgeBases} from '../../../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  AnalyticsDataPointPageViewPageTypesEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getAnalyticsReport} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {KnowledgeBaseReportSidePanelHeader} from './knowledgeBaseReportSidePanelHeader';
import {KnowledgeBaseReportSidePanelArticlePreview} from './sidepanel/knowledgeBaseReportSidePanelArticlePreview';
import {KnowledgeBaseReportSidePanelArticlesUpdated} from './sidepanel/knowledgeBaseReportSidePanelArticlesUpdated';
import {KnowledgeBaseReportSidePanelNoPreview} from './sidepanel/knowledgeBaseReportSidePanelNoPreview';

/*
 * Constants.
 */

export type KnowledgeBaseReportSidePanelSupportTypes =
  | AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES
  | AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS;

export interface KnowledgeBaseReportSidePanelSelectedItem {
  pageType: AnalyticsDataPointPageViewPageTypesEnum;
  id: number;
  shouldDisplayDraft?: boolean;
  locale?: KnowledgeBaseSupportedLanguagesTypesEnum;
}

/*
 * Props.
 */

interface KnowledgeBaseReportSidePanelProps {
  knowledgeBaseId: number;
  selectedMetricType?: KnowledgeBaseReportSidePanelSupportTypes;
  selectedItem?: KnowledgeBaseReportSidePanelSelectedItem;
  onRequestClose: () => void;
  onClearSelectedItem: () => void;
  onArticleSelect: ({
    articleId,
    shouldDisplayDraft,
  }: {
    articleId: number;
    shouldDisplayDraft: boolean;
  }) => void;
}

/*
 * Style.
 */

const StyledSidePanelContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  grid-column: 2;
  min-height: 0;
  overflow-y: auto;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanel: FC<KnowledgeBaseReportSidePanelProps> = ({
  knowledgeBaseId,
  selectedMetricType,
  selectedItem,
  onRequestClose,
  onArticleSelect,
  onClearSelectedItem,
}) => {
  const {navigateToLink} = useLinkNavigation();
  const knowledgeBase = useAppSelector(getKnowledgeBases)[knowledgeBaseId];
  const company = useAppSelector(getCompany);
  const report = useAppSelector(getAnalyticsReport);

  const renderContent = () => {
    if (selectedItem) {
      switch (selectedItem.pageType) {
        case AnalyticsDataPointPageViewPageTypesEnum.ARTICLE:
          return (
            <KnowledgeBaseReportSidePanelArticlePreview
              knowledgeBaseId={knowledgeBaseId}
              articleId={selectedItem.id}
              locale={selectedItem.locale}
              shouldDisplayDraft={selectedItem.shouldDisplayDraft}
            />
          );
        default:
          return (
            <KnowledgeBaseReportSidePanelNoPreview
              knowledgeBaseId={knowledgeBaseId}
              id={selectedItem.id}
              locale={selectedItem.locale}
              pageType={selectedItem.pageType}
            />
          );
      }
    }
    if (selectedMetricType === AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES) {
      return (
        <KnowledgeBaseReportSidePanelArticlesUpdated
          totalArticles={(report?.metrics as any)?.[selectedMetricType]?.value || 0}
          onArticleSelect={onArticleSelect}
        />
      );
    }
    return null;
  };

  const isArticleSelected = selectedItem?.pageType === AnalyticsDataPointPageViewPageTypesEnum.ARTICLE;
  const onArticleEdit = () => {
    if (!isArticleSelected || !knowledgeBase) {
      return;
    }

    navigateToLink(
      buildArticlePath(knowledgeBaseId, {articleId: selectedItem.id, locale: selectedItem.locale}),
    );
  };

  const onArticleView = () => {
    if (!isArticleSelected || !knowledgeBase) {
      return;
    }

    const isInternal = knowledgeBase.type === KnowledgeBaseAccessTypesEnum.INTERNAL;
    if (selectedItem.shouldDisplayDraft) {
      globalWindow.open(
        buildKnowledgeBaseArticlePreviewPath(
          company.url,
          knowledgeBaseId,
          selectedItem.id,
          undefined,
          undefined,
          selectedItem.locale,
          undefined,
          true,
        ),
      );
    } else if (isInternal) {
      globalWindow.open(
        buildPublishedInternalArticlePreviewLink(
          knowledgeBase,
          company.url,
          selectedItem.id,
          selectedItem.locale,
        ),
        '_blank',
      );
    } else {
      navigateToLink(buildPublicArticleUrl(knowledgeBase, selectedItem.id, selectedItem.locale));
    }
  };

  return (
    <StyledSidePanelContentDiv>
      <KnowledgeBaseReportSidePanelHeader
        title={renderMetricLabel(selectedMetricType, selectedItem)}
        onRequestClose={onRequestClose}
        onBackClick={onClearSelectedItem}
        shouldDisplayBackButton={Boolean(
          selectedMetricType === AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES && selectedItem,
        )}
        onEdit={isArticleSelected ? onArticleEdit : undefined}
        onView={isArticleSelected ? onArticleView : undefined}
      />
      {renderContent()}
    </StyledSidePanelContentDiv>
  );
};

/*
 * Helpers.
 */

function renderMetricLabel(
  metricType?: KnowledgeBaseReportSidePanelSupportTypes,
  selectedItem?: KnowledgeBaseReportSidePanelSelectedItem,
) {
  // If we have a selected article, we can display a custom title since both metrics and preview an article.
  if (selectedItem && selectedItem.pageType === AnalyticsDataPointPageViewPageTypesEnum.ARTICLE) {
    return <Trans id="analytics-knowledge-base-side-panel-article-preview-header">Article preview</Trans>;
  }

  switch (metricType) {
    case AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES:
      return <Trans id="analytics-knowledge-base-side-panel-articles-updated-header">Articles updated</Trans>;
    case AnalyticsTableMetricsEnum.ARTICLE_PAGE_VIEWS: {
      return <Trans id="analytics-knowledge-base-side-panel-page-preview-header">Page preview</Trans>;
    }
    default:
      return null;
  }
}
